import React from "react";
import { Clock, Calendar, CheckCircle, BookOpen, Play, XCircle, List, Filter } from "lucide-react";

export type HomeworkStatusFilter = "all" | "PENDING" | "STARTED" | "COMPLETED" | "FAILED";

interface HomeworkStatusFilterProps {
  selectedStatus: HomeworkStatusFilter;
  onStatusChange: (status: HomeworkStatusFilter) => void;
  startDate: string | null;
  endDate: string | null;
  onDateChange: (startDate: string | null, endDate: string | null) => void;
}

export default function HomeworkStatusFilter({
  selectedStatus,
  onStatusChange,
  startDate,
  endDate,
  onDateChange,
}: HomeworkStatusFilterProps) {
  const filterOptions = [
    {
      key: "all" as HomeworkStatusFilter,
      label: "Tüm Ödevler",
      icon: List,
      count: 0,
    },
    {
      key: "PENDING" as HomeworkStatusFilter,
      label: "Bekleyen Ödevler",
      icon: Clock,
      count: 0,
    },
    {
      key: "STARTED" as HomeworkStatusFilter,
      label: "Başlanan Ödevler",
      icon: Play,
      count: 0,
    },
    {
      key: "COMPLETED" as HomeworkStatusFilter,
      label: "Tamamlanan Ödevler",
      icon: CheckCircle,
      count: 0,
    },
    {
      key: "FAILED" as HomeworkStatusFilter,
      label: "Başarısız Ödevler",
      icon: XCircle,
      count: 0,
    },
  ];

  return (
    <div className="pr-2 w-64">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">Filtreler</span>
        </div>
        <div className="flex items-center gap-1" />
      </div>

      <label className="block text-xs font-medium text-gray-700 mb-2">Ödev Durumu</label>
      <div className="space-y-1">
        {filterOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedStatus === option.key;

          return (
            <button
              key={option.key}
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(option.key);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-left ${
                isSelected ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-gray-700 hover:bg-gray-100"
              }`}>
              <Icon className={`w-4 h-4 ${isSelected ? "text-blue-600" : "text-gray-500"}`} />
              <span className="flex-1">{option.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-700 mb-2">Tarih Aralığı</label>

        <div className="space-y-3">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Başlangıç Tarihi</label>
            <input
              type="date"
              value={startDate || ""}
              onChange={(e) => onDateChange(e.target.value || null, endDate)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Bitiş Tarihi</label>
            <input
              type="date"
              value={endDate || ""}
              onChange={(e) => onDateChange(startDate, e.target.value || null)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
            />
          </div>

          {(startDate || endDate) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDateChange(null, null);
              }}
              className="w-full text-sm text-red-600 hover:text-red-700 hover:bg-red-50 py-2 px-3 rounded-lg transition-colors font-medium border border-red-200 hover:border-red-300">
              Tarihleri Temizle
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
